import React, { useCallback } from 'react'
import './ExternalUpdates.scss'
import { Col, Container, Row } from 'react-bootstrap'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { formatDate } from '../../common/aifeatures/utils'
import { useAppSelector } from '../../../../../store/hooks'
import { useSyncStatuses } from './hooks'
import { SyncStatusView } from './models'
import { selectCurrentUser } from '../../../../../store/meetingSummary/user'
import { LinkButton } from '../../../../../components/Button/LinkButton'
import { getExternalLink } from './utils'
import { ExternalUpdateTitle } from './ExternalUpdateTitle'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { useTracking } from '../../common/tracking/hooks'
import { TrackEvent } from '../../common/tracking'

export const ExternalUpdates: React.FC = () => {
  const { syncStatuses } = useSyncStatuses()

  return (
    <div className='d-flex flex-column gap-2 zocks-py-1.25 zocks-px-1.25 h-100'>
      {syncStatuses.length === 0 ? (
        <div className='d-flex flex-column h-100 justify-content-center align-items-center'>
          <h4>No updates yet</h4>
          <div className='text-center'>
            <div>The complete update history will appear here with direct access links. Meeting summaries, tasks, notes, and more.</div>
            <div>All in one place.</div>
          </div>
        </div>
      ) : null}
      {syncStatuses.map((syncStatus) => (
        <div>
          <SyncStatusItem syncStatus={syncStatus} source='history' />
        </div>
      ))}
    </div>
  )
}

export const SyncStatusItem: React.FC<{
  syncStatus: SyncStatusView
  showStartLine?: boolean
  showEndLine?: boolean
  source: 'workflow' | 'history'
}> = ({ syncStatus, showStartLine = false, showEndLine = false, source }) => {
  const currentUser = useAppSelector(selectCurrentUser)

  const { trackingClient } = useTracking()

  const onClickOpenExternalLink = useCallback(
    () =>
      trackingClient.track({
        eventName: TrackEvent.CRM_LINK_CLICKED,
        eventDetails: {
          serviceId: syncStatus.serviceId,
          source: source,
        },
      }),
    [trackingClient, syncStatus.serviceId, source],
  )

  if (!IntegrationIcon) {
    return null
  }

  const externalLink = getExternalLink({ syncStatus, currentUser })

  return (
    <Container className=''>
      {showStartLine ? (
        <Row>
          <Col xs='auto'>
            <div className='d-flex justify-content-center external-updates-vertical-line--fixed-size my-1'>
              <div className='external-updates-vertical-line' />
            </div>
          </Col>
        </Row>
      ) : null}
      <Row className='py-3 sync-status-item zocks-border-0.5'>
        <Col xs='auto' className='d-flex align-items-center justify-content-center flex-column'>
          <div className='mb-2'>
            <IntegrationIcon serviceId={syncStatus.serviceId as IntegrationServiceId} width={'1rem'} height={'1rem'} />
          </div>
          <div className='external-updates-vertical-line' />
        </Col>
        <Col>
          <div>
            <ExternalUpdateTitle syncStatus={syncStatus} />
            <div>{formatDate(syncStatus.syncedAt)}</div>
            {!!externalLink && (
              <div className='mt-3'>
                <LinkButton
                  onClick={onClickOpenExternalLink}
                  variant='link'
                  className='sync-status-item-external-link button-medium m-0 justify-content-start'
                  relative='route'
                  target='_blank'
                  to={externalLink}
                >
                  <i className='bi bi-box-arrow-up-right' />
                  <span>Open in {syncStatus.serviceId}</span>
                </LinkButton>
              </div>
            )}
          </div>
        </Col>
      </Row>
      {showEndLine ? (
        <Row>
          <Col xs='auto'>
            <div className='d-flex justify-content-center external-updates-vertical-line--fixed-size my-1'>
              <div className='external-updates-vertical-line' />
            </div>
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}
